import { useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { emptyFor } from './scope-notice';
import { messageFor } from '../lib/errors';
import { formatDate, formatMoney } from '../ui/format';
import { Badge, Loading, Panel, type BadgeTone } from '../ui/Page';
import { DataTable, type Column, type SortState } from '../ui/DataTable';
import { Person } from '../ui/Avatar';
import { StoredAvatar } from '../ui/StoredAvatar';
import { useMembers } from '../ui/UserSelect';
import { useCreateIntent } from '../app/create-intent';
import { ExportGlyph, FilterGlyph, SearchGlyph } from './BoardIcons';
import { FilterBuilder } from './FilterBuilder';
import { NewContactDialog } from './NewRecordDialog';
import {
  exportEntity,
  listFields,
  searchEntity,
  type FilterGroup,
  type SearchCursor,
} from './api';

/**
 * The CRM list screens.
 *
 * Leads, contacts and opportunities are the same screen with different columns: a server-side search
 * with a cursor, sorted and filtered where the data is (context.md §134). Writing them three times
 * would produce three subtly different pagination bugs.
 *
 * They wear the chrome the funnel and the leads screen wear — the breadcrumb, the toolbar, the
 * export — because they are the same kind of screen and looking like three different products is
 * how a list stops being trusted.
 *
 * Companies keeps its own screen because it carries the filter builder.
 */

/** Mirrors the server's own cap, so the message can name the number the file actually stopped at. */
const EXPORT_LIMIT = 5000;

/** Rows per request. Large enough that most lists need one, small enough to render at once. */
const PAGE = 50;

const EMPTY_FILTER: FilterGroup = { combinator: 'AND', conditions: [] };

/**
 * The search box and the filter builder, as one query.
 *
 * `AND`, because they narrow rather than widen: somebody who typed a name *and* built a condition
 * means both. An empty half is left out entirely — an `AND` group containing an empty group matches
 * differently from no group at all, and the difference is a list that silently returns nothing.
 */
function combine(search: FilterGroup | undefined, conditions: FilterGroup): FilterGroup | undefined {
  const parts = [search, conditions.conditions.length > 0 ? conditions : undefined].filter(
    (part): part is FilterGroup => part !== undefined,
  );

  if (parts.length === 0) return undefined;
  if (parts.length === 1) return parts[0];

  return { combinator: 'AND', conditions: parts };
}

export interface LeadRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  company_name: string | null;
  source: string | null;
  status: string;
  score: number | null;
  owner_id?: string | null;
  created_at: string;
}

export interface ContactRow {
  id: string;
  first_name: string;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  job_title: string | null;
  company_id: string | null;
  owner_id: string | null;
  /** The stored copy of their profile picture, when one has ever arrived (Phase 101). */
  avatar_file_id: string | null;
  created_at: string;
}

export interface OpportunityRow {
  id: string;
  title: string;
  amount_cents: number | null;
  currency: string | null;
  status: string;
  stage_id: string | null;
  expected_close_date: string | null;
  stage_changed_at: string | null;
  closed_at: string | null;
  source: string | null;
  owner_id: string | null;
  created_at: string;
}

const OPPORTUNITY_TONE: Record<string, BadgeTone> = {
  OPEN: 'info',
  WON: 'positive',
  LOST: 'danger',
};

function EntityList<Row>({
  entity,
  title,
  description,
  columns,
  sortField,
  emptyKey,
  create,
}: {
  entity: 'contacts' | 'opportunities';
  title: string;
  description: string;
  columns: Column<Row>[];
  sortField: string;
  emptyKey: string;
  /** What the "+ Novo" button does. A screen with no way to add to it is a screen with a hole. */
  create: React.ReactNode;
}): React.JSX.Element {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [applied, setApplied] = useState('');
  const [sort, setSort] = useState<SortState>({ field: sortField, direction: 'desc' });
  const [exportError, setExportError] = useState<string | null>(null);

  /*
   * Search and the filter builder, kept apart.
   *
   * They answer different questions — "find this person" and "show me everyone who…" — and a
   * screen that only had the first made the second impossible without leaving for a saved view.
   * Both narrow the same query, so the export and the count follow whatever is on screen.
   */
  const [building, setBuilding] = useState(false);
  const [draft, setDraft] = useState<FilterGroup>(EMPTY_FILTER);
  const [conditions, setConditions] = useState<FilterGroup>(EMPTY_FILTER);

  const fields = useQuery({
    queryKey: ['fields', entity],
    queryFn: () => listFields(entity),
    enabled: building,
  });

  const filter = combine(applied ? searchFilter(applied) : undefined, conditions);

  /*
   * Paged from the server, not fetched once and sliced (context.md §134).
   *
   * This used to ask for fifty rows and hand them to a table that paginates locally, so a list of
   * three hundred contacts was the first fifty with a last page that looked like the end — while
   * the count above it said three hundred. The screen contradicted itself and the reader believed
   * the number.
   */
  const results = useInfiniteQuery({
    queryKey: [entity, applied, sort],
    queryFn: ({ pageParam }) =>
      searchEntity<Row>(entity, {
        ...(filter ? { filter } : {}),
        sort,
        limit: PAGE,
        includeCount: true,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: null as SearchCursor | null,
    getNextPageParam: (last) => last.next_cursor,
  });

  const rows = results.data?.pages.flatMap((page) => page.results) ?? undefined;
  const visibility = results.data?.pages[0]?.visibility;

  // The same filter and order the screen is showing, so the file and the list agree.
  const exporting = useMutation({
    mutationFn: () => exportEntity(entity, { ...(filter ? { filter } : {}), sort }),
    onSuccess: (outcome) => {
      setExportError(
        outcome.truncated ? t('crm.exportTruncated').replace('{n}', String(EXPORT_LIMIT)) : null,
      );
    },
    onError: (caught: unknown) => setExportError(messageFor(caught)),
  });

  // The count comes with the first page; later pages do not recount what has not changed.
  const total = results.data?.pages[0]?.total ?? null;

  return (
    <div className="view view--wide leads-page">
      <header className="board-head">
        <div>
          <nav className="breadcrumb" aria-label={title}>
            <span>{t('nav.crm')}</span>
            <span aria-hidden="true">/</span>
            <span className="strong">{title}</span>
          </nav>

          <h1>{title}</h1>
          <p className="muted">{description}</p>
        </div>

        <div className="cell-inline">{create}</div>
      </header>

      {exportError && (
        <p className="alert" role="alert">
          {exportError}
        </p>
      )}

      <Panel>
        <form
          className="panel-toolbar"
          onSubmit={(submitEvent) => {
            submitEvent.preventDefault();
            setApplied(search.trim());
          }}
        >
          <div className="panel-toolbar__title">
            <label className="board-search">
              <SearchGlyph />
              <input
                type="search"
                value={search}
                placeholder={t('crm.searchPlaceholder')}
                aria-label={t('crm.searchPlaceholder')}
                onChange={(changeEvent) => setSearch(changeEvent.target.value)}
              />
            </label>

            <button type="submit" className="button button--quiet button--compact">
              {t('crm.search')}
            </button>

            {applied && (
              <button
                type="button"
                className="chip"
                onClick={() => {
                  setSearch('');
                  setApplied('');
                }}
              >
                {t('crm.clearSearch')}
              </button>
            )}
          </div>

          <div className="panel-toolbar__actions">
            {total !== null && (
              <span className="muted small">
                {t('crm.results').replace('{count}', String(total))}
              </span>
            )}

            <button
              type="button"
              className={`button button--compact ${
                conditions.conditions.length > 0 ? 'button--primary' : 'button--quiet'
              }`}
              aria-expanded={building}
              onClick={() => setBuilding((current) => !current)}
            >
              <FilterGlyph />
              {conditions.conditions.length > 0
                ? t('filters.active').replace('{n}', String(conditions.conditions.length))
                : t('filters.title')}
            </button>

            <button
              type="button"
              className="button button--quiet button--compact"
              disabled={exporting.isPending}
              onClick={() => exporting.mutate()}
            >
              <ExportGlyph />
              {t('crm.export')}
            </button>
          </div>
        </form>

        {building && (
          <div className="stack">
            {fields.data ? (
              <FilterBuilder fields={fields.data.fields} value={draft} onChange={setDraft} />
            ) : (
              <Loading />
            )}

            <div className="filters__actions">
              <button
                type="button"
                className="button button--primary button--compact"
                onClick={() => setConditions(draft)}
              >
                {t('filters.apply')}
              </button>
              <button
                type="button"
                className="button button--quiet button--compact"
                onClick={() => {
                  setDraft(EMPTY_FILTER);
                  setConditions(EMPTY_FILTER);
                }}
              >
                {t('filters.clear')}
              </button>
            </div>
          </div>
        )}

        <DataTable
          /* One key per entity: contacts and opportunities have different columns, and a single
             key meant choosing columns on one silently reset the other. */
          tableKey={entity}
          rows={rows}
          columns={columns}
          rowKey={(row) => (row as { id: string }).id}
          isLoading={results.isLoading}
          error={results.error}
          onRetry={() => void results.refetch()}
          onRowClick={(row) => navigate(`/crm/registro/${entity}/${(row as { id: string }).id}`)}
          sort={{ value: sort, onChange: setSort }}
          // This screen searches on the server; a second box narrowing only the page in hand would
          // answer a different question and say so in the same words.
          quickFilter={false}
          more={{
            hasMore: results.hasNextPage,
            loading: results.isFetchingNextPage,
            onLoadMore: () => void results.fetchNextPage(),
          }}
          empty={emptyFor(visibility, {
            title: t(emptyKey),
            description: t('crm.emptyBody'),
          })}
        />
      </Panel>
    </div>
  );
}

/**
 * The toolbar's free-text box, as a filter the server understands.
 *
 * Deliberately the same `contains` the filter builder produces, so a search here and a filter there
 * mean the same thing rather than two different notions of "matches".
 */
function searchFilter(term: string): FilterGroup {
  return { combinator: 'AND', conditions: [{ field: 'name', operator: 'contains', value: term }] };
}

export function ContactsPage(): React.JSX.Element {
  const { options: people } = useMembers();
  const [creating, setCreating] = useState(false);

  // Arriving from the home screen's Criar menu opens the same dialog the button here opens.
  useCreateIntent(() => setCreating(true));

  const columns: Column<ContactRow>[] = [
    {
      key: 'name',
      header: t('crm.field.name'),
      sortField: 'first_name',
      text: (contact) => `${contact.first_name} ${contact.last_name ?? ''}`,
      /*
       * With their face, when this product has one (Phase 101).
       *
       * A list of names is a list of strangers. The picture comes from the messaging channel the
       * person actually writes on, is kept in the bucket like any other file, and falls back to
       * initials — so a contact who has never written, or who has no photograph, looks like a
       * contact rather than like a broken image.
       */
      render: (contact) => (
        <span className="cell-person">
          <StoredAvatar
            name={[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
            fileId={contact.avatar_file_id}
            size="sm"
          />
          <span className="cell-stack">
            <span className="strong">
              {[contact.first_name, contact.last_name].filter(Boolean).join(' ')}
            </span>

            {/*
              Only when there is one.

              A dash under every single name is a column of nothing pretending to be information,
              and it doubles the height of every row to say it. Most contacts arrive from a message
              and will never have a job title.
            */}
            {contact.job_title && <span className="muted small">{contact.job_title}</span>}
          </span>
        </span>
      ),
    },
    {
      key: 'email',
      header: t('crm.field.email'),
      render: (contact) => contact.email ?? '—',
    },
    {
      /*
       * Whichever number there is, not the one column somebody happened to pick.
       *
       * Contacts created from a channel carry their number in `mobile`; `phone` is the landline a
       * person types into the form. This column read `phone` alone, so an address book built from
       * WhatsApp — which is most of them — showed a wall of dashes next to contacts whose entire
       * identity *is* a phone number. Mobile first, because that is the one somebody would ring.
       */
      key: 'phone',
      header: t('crm.field.phone'),
      secondary: true,
      text: (contact) => contact.mobile ?? contact.phone ?? '',
      render: (contact) => contact.mobile ?? contact.phone ?? '—',
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      sortField: 'created_at',
      secondary: true,
      align: 'end',
      render: (contact) => formatDate(contact.created_at),
    },
    // Off by default; the chooser turns them on and the account remembers the choice.
    {
      key: 'mobile',
      header: t('crm.field.mobile'),
      hidden: true,
      render: (contact) => contact.mobile ?? '—',
    },
    {
      key: 'job_title',
      header: t('crm.field.jobTitle'),
      hidden: true,
      render: (contact) => contact.job_title ?? '—',
    },
    {
      key: 'owner_id',
      header: t('crm.owner'),
      hidden: true,
      text: (contact) => personOf(people, contact.owner_id).name,
      render: (contact) => <Person size="xs" {...personOf(people, contact.owner_id)} />,
    },
  ];

  return (
    <>
      <EntityList<ContactRow>
        entity="contacts"
        title={t('nav.contacts')}
        description={t('crm.contactsDescription')}
        columns={columns}
        sortField="created_at"
        emptyKey="crm.noContacts"
        // Contacts arrive in bulk more often than one at a time, so importing is not buried.
        create={
          <>
            <Link className="button button--quiet" to="/crm/importar">
              {t('crm.import')}
            </Link>

            <button
              type="button"
              className="button button--primary"
              onClick={() => setCreating(true)}
            >
              + {t('crm.newContact')}
            </button>
          </>
        }
      />

      {creating && <NewContactDialog onClose={() => setCreating(false)} />}
    </>
  );
}

export function OpportunitiesPage(): React.JSX.Element {
  const { options: people } = useMembers();

  const columns: Column<OpportunityRow>[] = [
    {
      key: 'title',
      header: t('crm.field.title'),
      sortField: 'title',
      render: (opportunity) => <span className="strong">{opportunity.title}</span>,
    },
    {
      key: 'amount',
      header: t('crm.field.amount'),
      sortField: 'amount_cents',
      align: 'end',
      text: (opportunity) => String(opportunity.amount_cents ?? ''),
      render: (opportunity) => formatMoney(opportunity.amount_cents, opportunity.currency ?? 'BRL'),
    },
    {
      key: 'status',
      header: t('crm.field.status'),
      sortField: 'status',
      text: (opportunity) => t(`crm.opportunity.${opportunity.status}`),
      render: (opportunity) => (
        <Badge tone={OPPORTUNITY_TONE[opportunity.status] ?? 'neutral'}>
          {t(`crm.opportunity.${opportunity.status}`)}
        </Badge>
      ),
    },
    {
      key: 'close',
      header: t('crm.field.expectedClose'),
      sortField: 'expected_close_date',
      secondary: true,
      render: (opportunity) => formatDate(opportunity.expected_close_date),
    },
    {
      key: 'created',
      header: t('common.createdAt'),
      sortField: 'created_at',
      secondary: true,
      align: 'end',
      render: (opportunity) => formatDate(opportunity.created_at),
    },
    {
      key: 'stage_changed_at',
      header: t('crm.field.inStageSince'),
      hidden: true,
      sortField: 'stage_changed_at',
      render: (opportunity) => formatDate(opportunity.stage_changed_at),
    },
    {
      key: 'closed_at',
      header: t('crm.field.closedAt'),
      hidden: true,
      sortField: 'closed_at',
      render: (opportunity) => formatDate(opportunity.closed_at),
    },
    {
      key: 'source',
      header: t('record.field.source'),
      hidden: true,
      render: (opportunity) => opportunity.source ?? '—',
    },
    {
      key: 'owner_id',
      header: t('crm.owner'),
      hidden: true,
      text: (opportunity) => personOf(people, opportunity.owner_id).name,
      render: (opportunity) => <Person size="xs" {...personOf(people, opportunity.owner_id)} />,
    },
  ];

  return (
    <EntityList<OpportunityRow>
      entity="opportunities"
      title={t('nav.opportunities')}
      description={t('crm.opportunitiesDescription')}
      columns={columns}
      sortField="created_at"
      emptyKey="crm.noOpportunities"
      /* Deals are created in the funnel, in a stage, because a deal with no stage is not a deal.
         One place to create them beats a second form that has to invent which stage it lands in. */
      create={
        <Link to="/funil?criar=1" className="button button--primary">
          + {t('board.newDeal')}
        </Link>
      }
    />
  );
}

/** A person by id, or the honest shape of nobody. Used by the optional owner columns. */
function personOf(
  people: readonly { id: string; name: string; avatarUrl?: string | null }[],
  id: string | null,
): { name: string; url?: string | null; muted: boolean } {
  const found = id === null ? undefined : people.find((person) => person.id === id);

  return found
    ? { name: found.name, url: found.avatarUrl, muted: false }
    : { name: t('crm.unassigned'), muted: true };
}
